import { PageMeta } from "@/components/common/PageMeta";
import { PageHero } from "@/components/common/PageHero";
import { SignalMotif } from "@/components/common/SignalMotif";
import { HERO_ALT } from "@/lib/content";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ExternalLink } from "lucide-react";

const IMG_BASE = "/assets/dbrf-heritage-archival";
const CAPTION =
  "From left to right: Aleksandr M. Prokhorov, Nobel Laureate in Physics; Nikolay G. Basov, Nobel Laureate in Physics; Anatoliy N. Oraevsky, Professor and Doctor of Physical and Mathematical Sciences; and Yuri M. Popov, Professor and Doctor of Physical and Mathematical Sciences. Photograph supplied by the DBRF archive.";
const ALT =
  "Historical scientific meeting with Aleksandr Prokhorov, Nikolay Basov, Anatoliy Oraevsky and Yuri Popov seated and standing around a conference table before a chalkboard.";

const PROFILES = [
  {
    n: "01", name: "Aleksandr M. Prokhorov", role: "Physicist · Nobel Prize in Physics, 1964",
    copy: "Prokhorov and Basov conducted foundational work in quantum electronics that led to oscillators and amplifiers based on the maser–laser principle. Their work helped establish the theoretical and experimental foundations of coherent electromagnetic generation.",
    tags: ["Quantum electronics", "Maser–laser principle", "Coherent generation", "Nobel Prize, 1964"],
  },
  {
    n: "02", name: "Nikolay G. Basov", role: "Physicist · Nobel Prize in Physics, 1964",
    copy: "Basov’s research extended from molecular generators and quantum radiophysics to lasers, semiconductor systems and the interaction of intense coherent radiation with matter. His scientific leadership helped establish a major research environment for laser physics at the P. N. Lebedev Physical Institute.",
    tags: ["Quantum radiophysics", "Molecular generators", "Laser physics", "Semiconductor lasers"],
  },
  {
    n: "03", name: "Anatoliy N. Oraevsky", role: "Professor · Doctor of Physical and Mathematical Sciences",
    copy: "Oraevsky worked across molecular generators, laser physics, chemical lasers, laser chemistry and nonlinear dynamics. In 1963, Basov and Oraevsky showed how rapid processes with different molecular-energy relaxation rates could produce population inversion—a foundational contribution to the theory of chemical and gas-dynamic lasers.",
    copy2: "His later work examined nonlinear single-mode laser behaviour, dynamical chaos, coherent states and the relationship between chemical reactions and laser parameters.",
    tags: ["Chemical lasers", "Laser chemistry", "Molecular generators", "Nonlinear dynamics", "Dynamical chaos"],
  },
  {
    n: "04", name: "Yuri M. Popov", role: "Professor · Doctor of Physical and Mathematical Sciences",
    copy: "Popov contributed to research on coherent light–matter interaction, self-induced transparency and semiconductor laser systems. His published work addressed phase-modulation effects, resonant pulse propagation and the development and applications of injection lasers.",
    tags: ["Injection lasers", "Semiconductor lasers", "Self-induced transparency", "Phase modulation", "Coherent interaction"],
  },
];

const PROGRESSION = [
  ["Stimulated emission", "Using controlled transitions between energy states to produce coherent radiation."],
  ["Quantum oscillation and amplification", "Establishing the operating principles of generators and amplifiers based on quantum transitions."],
  ["Maser and laser systems", "Translating quantum-electronic theory into coherent microwave and optical systems."],
  ["Chemical and semiconductor lasers", "Extending coherent generation across molecular reactions and solid-state electronic structures."],
  ["Phase modulation and coherent interaction", "Investigating how resonant pulses, phase and matter interact dynamically."],
  ["Nonlinear dynamics and synchronisation", "Studying stability, self-organisation, modulation and chaotic regimes in laser systems."],
];

const SOURCES = [
  { t: "The Nobel Prize in Physics 1964 — Nobel Prize", u: "https://www.nobelprize.org/prizes/physics/1964/summary/" },
  { t: "Aleksandr M. Prokhorov — Nobel Prize facts", u: "https://www.nobelprize.org/prizes/physics/1964/prokhorov/facts/" },
  { t: "Nicolay G. Basov — Nobel Prize facts", u: "https://www.nobelprize.org/prizes/physics/1964/basov/facts/" },
  { t: "50 Years Since the Creation of the Laser — P. N. Lebedev Physical Institute", u: "https://www.lebedev.ru/ru/main-news/news/516" },
  { t: "Dynamics of Single-Mode Lasers and Dynamical Chaos — Anatoly N. Oraevsky", u: "https://doi.org/10.18500/0869-6632-1996-4-1-3-32" },
  { t: "Semiconductor Lasers — N. G. Basov, P. G. Eliseev and Yu. M. Popov", u: "https://doi.org/10.1070/PU1986v029n01ABEH003078" },
  { t: "Self-Induced Transparency Effect — I. A. Poluektov, Yu. M. Popov and V. S. Roitberg", u: "https://doi.org/10.1070/PU1975v017n05ABEH004365" },
  { t: "The Early History of the Injection Laser — Yu. M. Popov", u: "https://doi.org/10.3367/UFNe.0181.201101m.0102" },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ImageObject",
      contentUrl: "https://emergent-foundation-1.preview.emergentagent.com/assets/DBRF_Heritage_Archival_Final_Colour_4K.png",
      caption: CAPTION, width: 2000, height: 1277,
    },
    { "@type": "Person", name: "Aleksandr M. Prokhorov", award: "Nobel Prize in Physics, 1964" },
    { "@type": "Person", name: "Nikolay G. Basov", award: "Nobel Prize in Physics, 1964" },
    { "@type": "Person", name: "Anatoliy N. Oraevsky" },
    { "@type": "Person", name: "Yuri M. Popov" },
    { "@type": "ScholarlyArticle", name: "Dynamics of Single-Mode Lasers and Dynamical Chaos", url: "https://doi.org/10.18500/0869-6632-1996-4-1-3-32" },
    { "@type": "ScholarlyArticle", name: "Semiconductor Lasers", url: "https://doi.org/10.1070/PU1986v029n01ABEH003078" },
    { "@type": "ScholarlyArticle", name: "Self-Induced Transparency Effect", url: "https://doi.org/10.1070/PU1975v017n05ABEH004365" },
    { "@type": "ScholarlyArticle", name: "The Early History of the Injection Laser", url: "https://doi.org/10.3367/UFNe.0181.201101m.0102" },
  ],
};

function Tag({ children }) {
  return <span className="rounded-full border border-[var(--line)] bg-[var(--ivory)] px-2.5 py-1 font-mono text-[0.64rem] text-[var(--mineral)]">{children}</span>;
}

export default function Heritage() {
  return (
    <>
      <PageMeta
        fullTitle="Scientific Heritage | DBRF"
        description="Explore the scientific lineage connecting DBRF with pioneering work in quantum electronics, laser physics, chemical lasers, semiconductor systems and nonlinear dynamics."
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(JSONLD) }} />

      <PageHero
        eyebrow="Scientific heritage"
        title="From fundamental science to new technical possibility."
        intro="DBRF draws inspiration from a scientific lineage associated with pioneering work in quantum electronics and molecular approaches to computation. The purpose of this heritage is not nostalgia; it is to preserve a way of working in which deep theory and practical invention reinforce one another."
        image="/assets/06-heritage-hero.webp"
        alt={HERO_ALT.heritage}
        objectPosition="center 40%"
      />

      {/* Scientific lineage */}
      <section className="container-dbrf py-16 lg:py-24" data-testid="scientific-lineage">
        <div className="reading">
          <p className="eyebrow mb-4">Scientific lineage</p>
          <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.9rem,3.2vw,3rem)", lineHeight: 1.06 }}>
            Four scientists at the frontier of coherent light
          </h2>
          <p className="mt-5 text-lg text-[var(--mineral)]" style={{ lineHeight: 1.7 }}>
            This photograph brings together four physicists whose work helped shape the development of
            quantum electronics, lasers and the study of coherent and nonlinear physical systems. Their
            research environment joined fundamental theory with experimental physics and engineering—an
            approach that continues to inform DBRF’s commitment to multidisciplinary science and working
            technology.
          </p>
        </div>

        <figure className="mt-10">
          <div className="overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--ivory)]" style={{ aspectRatio: "2000 / 1277" }}>
            <picture>
              <source type="image/avif" sizes="(max-width: 1023px) 100vw, 1120px" srcSet={`${IMG_BASE}-768.avif 768w, ${IMG_BASE}-1024.avif 1024w, ${IMG_BASE}-1600.avif 1600w`} />
              <source type="image/webp" sizes="(max-width: 1023px) 100vw, 1120px" srcSet={`${IMG_BASE}-768.webp 768w, ${IMG_BASE}-1024.webp 1024w, ${IMG_BASE}-1600.webp 1600w, ${IMG_BASE}.webp 2000w`} />
              <img
                src={`${IMG_BASE}-1600.webp`}
                alt={ALT}
                width="2000"
                height="1277"
                loading="lazy"
                decoding="async"
                className="h-full w-full object-contain"
                data-testid="heritage-photo"
              />
            </picture>
          </div>
          <figcaption className="mx-auto mt-4 max-w-3xl text-center text-sm text-[var(--mineral)]" data-testid="heritage-caption">
            {CAPTION}
          </figcaption>
        </figure>

        <SignalMotif className="mx-auto mt-12 max-w-4xl opacity-80" height={70} />

        {/* Profiles */}
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {PROFILES.map((p) => (
            <div key={p.n} className="card-lift rounded-2xl border border-[var(--line)] bg-[var(--paper)] p-7" data-testid={`profile-${p.n}`}>
              <div className="flex items-baseline gap-3">
                <span className="font-mono text-sm text-[var(--cobalt)]">{p.n}</span>
                <h3 className="font-display text-xl font-bold text-navy">{p.name}</h3>
              </div>
              <p className="mt-1 font-mono text-xs uppercase tracking-wide text-[var(--mineral)]">{p.role}</p>
              <p className="mt-4 text-sm text-[var(--mineral)]" style={{ lineHeight: 1.7 }}>{p.copy}</p>
              {p.copy2 && <p className="mt-3 text-sm text-[var(--mineral)]" style={{ lineHeight: 1.7 }}>{p.copy2}</p>}
              <div className="mt-4 flex flex-wrap gap-2">{p.tags.map((t) => <Tag key={t}>{t}</Tag>)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Collective contribution */}
      <section className="border-y border-[var(--line)] bg-[var(--paper)] py-16 lg:py-24" data-testid="collective-contribution">
        <div className="container-dbrf">
          <div className="reading">
            <p className="eyebrow mb-4">Collective contribution</p>
            <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.7rem,2.8vw,2.6rem)", lineHeight: 1.08 }}>
              From quantum electronics to complex coherent systems
            </h2>
            <p className="mt-5 text-[var(--mineral)]" style={{ lineHeight: 1.7 }}>
              The work represented by these scientists spans several connected stages in the evolution of
              modern photonics: the principles of quantum amplification, molecular and semiconductor
              generation, chemical conversion into coherent radiation, resonant light–matter interaction
              and the nonlinear dynamics of laser systems.
            </p>
          </div>

          <ol className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-6" data-testid="progression">
            {PROGRESSION.map(([t, d], i) => (
              <li key={t} className="rounded-2xl border border-[var(--line)] bg-[var(--ivory)] p-5">
                <span className="font-mono text-xs text-[var(--cobalt)]">{String(i + 1).padStart(2, "0")}</span>
                <p className="mt-1 font-display text-sm font-bold text-navy">{t}</p>
                <p className="mt-2 text-xs text-[var(--mineral)]" style={{ lineHeight: 1.6 }}>{d}</p>
              </li>
            ))}
          </ol>

          <p className="mt-6 text-xs text-[var(--mineral)]">
            Basov and Prokhorov shared half of the 1964 Nobel Prize in Physics, with the other half
            awarded to Charles H. Townes. The photograph identification is supplied by the DBRF archive
            and does not by itself establish a specific collaboration; not every technology listed was
            developed jointly by all four scientists.
          </p>
        </div>
      </section>

      {/* Modern relevance */}
      <section className="container-dbrf py-16 lg:py-24">
        <div className="rounded-3xl border border-[var(--cobalt)]/25 bg-[var(--cobalt)]/5 p-8 lg:p-12" data-testid="modern-relevance">
          <p className="eyebrow mb-4">Modern relevance</p>
          <h2 className="font-display font-bold text-navy" style={{ fontSize: "clamp(1.7rem,2.8vw,2.6rem)" }}>
            Coherence is still an engineering problem
          </h2>
          <p className="reading mt-5 text-lg text-[var(--mineral)]" style={{ lineHeight: 1.7 }}>
            Modern AI, communications, sensing and distributed computing operate in a very different
            technological environment, but they continue to depend on precise control of signals, phase,
            structure, timing and synchronisation. DBRF draws inspiration from this scientific tradition:
            understand the underlying dynamics, preserve meaningful structure and convert theory into
            systems that can be tested and used.
          </p>
          <p className="reading mt-5 text-sm text-[var(--mineral)]" style={{ lineHeight: 1.7 }}>
            Their research in coherent generation, phase behaviour, laser stability and nonlinear dynamics
            forms part of the broader scientific foundation from which modern frequency control, signal
            synchronisation and precision timing technologies evolved.
          </p>
          <p className="reading mt-4 text-xs text-[var(--mineral)]">
            This is a statement of scientific and methodological continuity. It does not claim that the
            individuals shown in the photograph developed today’s digital timing standards, AI
            architectures or DBRF technologies.
          </p>
        </div>
      </section>

      {/* Sources */}
      <section className="border-t border-[var(--line)] bg-[var(--paper)] py-16 lg:py-20">
        <div className="container-dbrf reading">
          <Accordion type="single" collapsible defaultValue="sources" data-testid="sources-accordion">
            <AccordionItem value="sources" className="border-[var(--line)]">
              <AccordionTrigger className="text-left font-display text-lg font-bold text-navy hover:no-underline" data-testid="sources-trigger">
                Sources and further reading
              </AccordionTrigger>
              <AccordionContent>
                <ul className="mt-2 space-y-3">
                  {SOURCES.map((s) => (
                    <li key={s.u}>
                      <a href={s.u} target="_blank" rel="noopener noreferrer" data-testid="source-link"
                        className="group inline-flex items-start gap-2 text-sm text-[var(--cobalt)] hover:text-[var(--navy)]">
                        <ExternalLink size={14} className="mt-0.5 flex-shrink-0" />
                        <span className="link-underline">{s.t}</span>
                      </a>
                    </li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-[var(--mineral)]">
                  Summaries above are original DBRF text. External sources open in a new tab.
                </p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </>
  );
}
